import { Upload, BarChart3, Download, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const steps = [
  {
    icon: Upload,
    step: "01",
    title: "Import or Start Fresh",
    description: "Copy-paste your resume text, upload a PDF/DOCX, or build one from scratch. No complicated setup.",
  },
  {
    icon: BarChart3,
    step: "02",
    title: "Customize & Score",
    description: "Toggle sections, reorder content, save your links, and let AI optimize your resume for ATS compatibility.",
  },
  {
    icon: Download,
    step: "03",
    title: "Download & Apply Fast",
    description: "Export as PDF or DOCX with auto-naming. Apply to multiple jobs in minutes with different resume versions.",
  },
];

const HowItWorks = () => {
  return (
    <section className="py-20 relative bg-secondary/30">
      <div className="w-full px-6 sm:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4">
            Simple Process
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            From Zero to Applied in{" "}
            <span className="text-gradient">3 Steps</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            No learning curve. Get your optimized resume and start applying in under 2 minutes.
          </p>
        </motion.div>

        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6 relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-0.5 bg-gradient-to-r from-primary/40 via-primary/20 to-primary/40" />

            {steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                className="relative group"
              >
                <div className="flex flex-col items-center text-center p-8 rounded-2xl bg-card border border-border/50 hover:border-primary/30 hover:shadow-lg transition-all duration-300">
                  <div className="relative mb-6">
                    <div className="w-20 h-20 rounded-2xl gradient-primary flex items-center justify-center shadow-xl group-hover:shadow-glow transition-all duration-500 group-hover:scale-105">
                      <step.icon className="w-9 h-9 text-primary-foreground" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-foreground text-background text-sm font-bold flex items-center justify-center shadow-lg">
                      {step.step}
                    </div>
                  </div>
                  <h3 className="font-heading font-bold text-xl mb-3 text-foreground">{step.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{step.description}</p>
                </div>

                {i < steps.length - 1 && (
                  <div className="flex justify-center my-4 md:hidden">
                    <ArrowRight className="w-5 h-5 text-primary/50 rotate-90" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
